import {
    Box,
    Button,
    Flex,
    Grid,
    Image,
    Text,
    VStack,
} from "@chakra-ui/react";
import { motion, useReducedMotion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import LogOutOnly from "../../components/Auth/LogOutOnly";
import Footer from "../../components/Layout/Footer";
import { guestSignUp, saveGuestAccessToken } from "../../api";
import { useAuthStore } from "../../store";
import { colors } from "../../styles";

const GRAIN_BG =
    "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

const HERO_EASE = [0.22, 1, 0.36, 1] as const;
const HERO_STAGGER = 0.14;
const HERO_ENTER_Y = 24;
const HERO_DURATION = 0.85;
const HERO_INITIAL_DELAY = 0.2;

function HeroFadeIn({
    index,
    reduceMotion,
    children,
}: {
    index: number;
    reduceMotion: boolean;
    children: ReactNode;
}) {
    return (
        <motion.div
            initial={{ opacity: 0, y: reduceMotion ? 0 : HERO_ENTER_Y }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
                duration: reduceMotion ? 0.25 : HERO_DURATION,
                ease: HERO_EASE,
                delay: reduceMotion ? 0 : index * HERO_STAGGER + HERO_INITIAL_DELAY,
            }}
        >
            {children}
        </motion.div>
    );
}

/** 캡처 이미지 비율 (width / height). 이미지 원본과 맞추면 여백 없이 표시됩니다 */
const CAPTURE_ASPECT_RATIO = 16 / 10;
const CAPTURE_HOLD_MS = 4000;
const CAPTURE_CROSSFADE_S = 0.9;
const CAPTURE_CROSSFADE_EASE = `cubic-bezier(${HERO_EASE.join(", ")})`;

type CaptureKey = "A" | "B";

function preloadImage(src: string) {
    return new Promise<void>((resolve) => {
        const img = new window.Image();
        img.onload = () => resolve();
        img.onerror = () => resolve();
        img.src = src;
    });
}

function waitForNextPaint() {
    return new Promise<void>((resolve) => {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => resolve());
        });
    });
}

const captureImageSx = {
    willChange: "opacity",
    backfaceVisibility: "hidden",
    transform: "translateZ(0)",
} as const;

interface BrowserFrameProps {
    captureSrc?: string;
    alt: string;
    children?: ReactNode;
}

function BrowserFrame({ captureSrc, alt, children }: BrowserFrameProps) {
    return (
        <Box
            w="100%"
            maxW="100%"
            borderRadius="12px"
            border="1px solid"
            borderColor="blackAlpha.100"
            bg={colors.white}
            boxShadow="0 25px 50px -12px rgba(0, 32, 96, 0.18)"
            overflow="hidden"
        >
            <Flex
                align="center"
                gap={3.5}
                px={3.5}
                py={3}
                flexShrink={0}
                borderBottom="0.1px solid"
                borderColor={colors.g4}
                bg="rgba(255, 255, 255, 0.72)"
            >
                <Flex gap={1.5} flexShrink={0} aria-hidden="true">
                    <Box w="9px" h="9px" borderRadius="full" bg="#ff5f57" />
                    <Box w="9px" h="9px" borderRadius="full" bg="#febc2e" />
                    <Box w="9px" h="9px" borderRadius="full" bg="#28c841" />
                </Flex>
            </Flex>

            <Box
                w="100%"
                aspectRatio={CAPTURE_ASPECT_RATIO}
                position="relative"
                bg={colors.white}
                isolation="isolate"
            >
                {children ??
                    (captureSrc ? (
                        <Image
                            src={captureSrc}
                            alt={alt}
                            position="absolute"
                            inset={0}
                            w="100%"
                            h="100%"
                            objectFit="contain"
                            objectPosition="center"
                            draggable={false}
                            loading="eager"
                            decoding="async"
                        />
                    ) : null)}
            </Box>
        </Box>
    );
}

interface BrowserPlaceholderProps {
    captureSrcA?: string;
    captureSrcB?: string;
}

function BrowserPlaceholder({
    captureSrcA,
    captureSrcB,
}: BrowserPlaceholderProps) {
    const reduceMotion = useReducedMotion();
    const [ready, setReady] = useState(false);
    const [front, setFront] = useState<CaptureKey>("A");
    const [frontOpacity, setFrontOpacity] = useState(1);

    const slides = {
        A: { src: captureSrcA, alt: "서비스 화면 미리보기 A" },
        B: { src: captureSrcB, alt: "서비스 화면 미리보기 B" },
    } as const;

    useEffect(() => {
        let cancelled = false;
        const sources = [captureSrcA, captureSrcB].filter(
            (src): src is string => Boolean(src),
        );

        if (sources.length === 0) {
            setReady(true);
            return;
        }

        Promise.all(sources.map(preloadImage)).then(() => {
            if (!cancelled) setReady(true);
        });

        return () => {
            cancelled = true;
        };
    }, [captureSrcA, captureSrcB]);

    useEffect(() => {
        if (reduceMotion || !ready || !captureSrcA || !captureSrcB) return;

        let cancelled = false;
        let timeoutId = 0;
        let showing: CaptureKey = "A";

        const wait = (ms: number) =>
            new Promise<void>((resolve) => {
                timeoutId = window.setTimeout(resolve, ms);
            });

        const loop = async () => {
            while (!cancelled) {
                await wait(CAPTURE_HOLD_MS);
                if (cancelled) return;

                const next: CaptureKey = showing === "A" ? "B" : "A";
                setFront(next);
                setFrontOpacity(0);

                await waitForNextPaint();
                if (cancelled) return;

                setFrontOpacity(1);
                await wait(CAPTURE_CROSSFADE_S * 1000);
                if (cancelled) return;

                showing = next;
            }
        };

        void loop();

        return () => {
            cancelled = true;
            window.clearTimeout(timeoutId);
        };
    }, [reduceMotion, ready, captureSrcA, captureSrcB]);

    if (reduceMotion || !captureSrcB) {
        return <BrowserFrame captureSrc={captureSrcA} alt={slides.A.alt} />;
    }

    return (
        <BrowserFrame alt={slides[front].alt}>
            {(["A", "B"] as const).map((key) => {
                const slide = slides[key];
                if (!slide.src) return null;

                const isFront = key === front;

                return (
                    <Image
                        key={key}
                        src={slide.src}
                        alt={isFront ? slide.alt : ""}
                        position="absolute"
                        inset={0}
                        w="100%"
                        h="100%"
                        objectFit="contain"
                        objectPosition="center"
                        draggable={false}
                        loading="eager"
                        decoding="async"
                        pointerEvents="none"
                        opacity={isFront ? frontOpacity : 1}
                        zIndex={isFront ? 1 : 0}
                        transition={
                            isFront && frontOpacity === 1
                                ? `opacity ${CAPTURE_CROSSFADE_S}s ${CAPTURE_CROSSFADE_EASE}`
                                : "none"
                        }
                        sx={captureImageSx}
                    />
                );
            })}
        </BrowserFrame>
    );
}

export default function Welcome() {
    const { setIsLoggedIn, setIsAdmin, setIsGuest } = useAuthStore();
    const navigate = useNavigate();
    const reduceMotion = useReducedMotion();

    const guestSignUpMutation = useMutation({
        mutationFn: guestSignUp,
        onSuccess: async (data) => {
            const { server_token } = data;

            if (server_token) {
                await saveGuestAccessToken(server_token);
            }

            setIsLoggedIn(true);
            setIsAdmin(false);
            setIsGuest(true);
            navigate("/");
        },
        onError: (error) => {
            console.log("error!", error);
        },
    });

    const handleGuestSignUp = async () => {
        await guestSignUpMutation.mutateAsync();
    };

    const pillButtonBase = {
        h: "44px",
        minW: { base: "100px", sm: "120px" },
        px: { base: 4, md: 5 },
        borderRadius: "9999px",
        fontSize: { base: "sm", md: "md" },
        fontWeight: "600",
        flex: { base: "1 1 auto", sm: "0 1 auto" },
    };

    return (
        <LogOutOnly>
            <Box
                position="relative"
                minH="100vh"
                w="100%"
                overflowX="hidden"
                sx={{
                    backgroundColor: colors.white,
                    backgroundImage: `
                        radial-gradient(ellipse 90% 70% at 8% 12%, rgba(0, 32, 96, 0.28) 0%, transparent 62%),
                        radial-gradient(ellipse 85% 75% at 92% 88%, rgba(255, 121, 19, 0.58) 0%, transparent 72%),
                        radial-gradient(ellipse 65% 55% at 78% 15%, rgba(255, 121, 19, 0.42) 0%, transparent 68%),
                        radial-gradient(ellipse 50% 50% at 18% 78%, rgba(0, 32, 96, 0.20) 0%, transparent 56%),
                        radial-gradient(ellipse 80% 80% at 50% 50%, ${colors.white} 0%, transparent 100%)
                    `,
                }}
            >
                <Box
                    position="absolute"
                    inset={0}
                    pointerEvents="none"
                    opacity={0.35}
                    mixBlendMode="overlay"
                    bgImage={GRAIN_BG}
                    bgSize="128px 128px"
                />

                <Flex
                    position="relative"
                    zIndex={1}
                    direction="column"
                    minH="100vh"
                >
                    <Box
                        flex="1"
                        w="100%"
                        maxW="1024px"
                        mx="auto"
                        px={{ base: 5, md: 8 }}
                        py={{ base: 10, md: 16 }}
                        display="flex"
                        alignItems="center"
                    >
                    <Grid
                        w="100%"
                        templateColumns={{ base: "1fr", lg: "1fr 1fr" }}
                        gap={{ base: 10, lg: 14 }}
                        alignItems="center"
                    >
                        <VStack
                            align={{ base: "center", lg: "flex-start" }}
                            spacing={{ base: 6, md: 7 }}
                            textAlign={{ base: "center", lg: "left" }}
                        >
                            <HeroFadeIn index={0} reduceMotion={reduceMotion ?? false}>
                                <Image
                                    src="png/logo.png"
                                    alt="logo"
                                    w={{ base: "240px", md: "280px", lg: "300px" }}
                                    h="auto"
                                    maxH="125px"
                                    objectFit="contain"
                                />
                            </HeroFadeIn>

                            <HeroFadeIn index={1} reduceMotion={reduceMotion ?? false}>
                                <Text
                                    fontSize={{ base: "md", md: "md" }}
                                    lineHeight="1.65"
                                    color={colors.black}
                                    maxW="md"
                                    fontWeight={500}
                                >
                                    약 검색은 3초, 약사를 잇는 곳
                                </Text>
                            </HeroFadeIn>

                            <Flex
                                direction="row"
                                gap={{ base: 2, sm: 3 }}
                                flexWrap="wrap"
                                justify={{ base: "center", lg: "flex-start" }}
                                w="100%"
                                maxW={{ base: "100%", md: "520px" }}
                            >
                                <HeroFadeIn index={2} reduceMotion={reduceMotion ?? false}>
                                    <Button
                                        {...pillButtonBase}
                                        variant="outline"
                                        bg={colors.white}
                                        color={colors.blue}
                                        border="1.5px solid"
                                        borderColor={colors.blue}
                                        _hover={{ bg: colors.light }}
                                        onClick={handleGuestSignUp}
                                        isLoading={guestSignUpMutation.isPending}
                                    >
                                        <Text
                                            fontSize={{ base: "sm", md: "sm" }}
                                            lineHeight="1.65"
                                            color={colors.blue}
                                            maxW="sm"
                                        >
                                            비회원 로그인
                                        </Text>
                                    </Button>
                                </HeroFadeIn>

                                <HeroFadeIn index={3} reduceMotion={reduceMotion ?? false}>
                                    <Button
                                        {...pillButtonBase}
                                        as={Link}
                                        to="/signin"
                                        bg={colors.blue}
                                        color={colors.white}
                                        border="1.5px solid"
                                        borderColor={colors.blue}
                                        _hover={{ bg: colors.blue }}
                                    >
                                        <Text
                                            fontSize={{ base: "sm", md: "sm" }}
                                            lineHeight="1.65"
                                            color={colors.white}
                                            maxW="sm"
                                        >
                                            로그인
                                        </Text>
                                    </Button>
                                </HeroFadeIn>

                                <HeroFadeIn index={4} reduceMotion={reduceMotion ?? false}>
                                    <Button
                                        {...pillButtonBase}
                                        as={Link}
                                        to="/signup"
                                        variant="outline"
                                        bg={colors.white}
                                        color={colors.blue}
                                        border="1.5px solid"
                                        borderColor={colors.blue}
                                        _hover={{ bg: colors.light }}
                                    >
                                        <Text
                                            fontSize={{ base: "sm", md: "sm" }}
                                            lineHeight="1.65"
                                            color={colors.blue}
                                            maxW="sm"
                                        >
                                            회원가입
                                        </Text>
                                    </Button>
                                </HeroFadeIn>
                            </Flex>
                        </VStack>

                        <HeroFadeIn index={5} reduceMotion={reduceMotion ?? false}>
                            <Box w="100%" maxW="100%">
                                <BrowserPlaceholder
                                    captureSrcA="png/screenshot_search.png"
                                    captureSrcB="png/screenshot_chat.png"
                                />
                            </Box>
                        </HeroFadeIn>
                    </Grid>
                    </Box>
                    <Footer />
                </Flex>
            </Box>
        </LogOutOnly>
    );
}
